// Insurance / Licence tab — orchestrates list + CRUD + archive + renew for
// insurance tracking items under a contract. Uses TrackingTabShell chrome
// and TrackingTable row container. Deep-link (`?item=X`) resolves to:
//   - in-page match: inline-expand the row + ring highlight + scroll
//   - not-in-page/archived match: non-auto-toggling Alert banner (F3)
//     with a "View archived item" CTA → InsuranceDetailModal.
// Post-Create: new item becomes highlightRowId (F5) — caught by
// TrackingTable's scroll-into-view.

import { useMemo, useRef, useState } from "react";
import {
  Pencil,
  Archive as ArchiveIcon,
  RotateCw,
  AlertTriangle,
} from "lucide-react";
import Alert from "../../../common/Alert";
import Button from "../../../common/Button";
import TrackingTabShell from "../shared/TrackingTabShell";
import { useDeepLinkPageJump } from "../shared/useDeepLinkPageJump";
import TrackingPagination from "../shared/TrackingPagination";
import TrackingTable, { type Column, type ColumnSort } from "../shared/TrackingTable";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import ArchiveDialog from "../shared/ArchiveDialog";
import { useTableSort } from "../shared/useTableSort";
import InsuranceFormModal, {
  type InsuranceFormMode,
} from "./InsuranceFormModal";
import InsuranceInlineExpand from "./InsuranceInlineExpand";
import InsuranceDetailExpand from "./InsuranceDetailExpand";
import InsuranceDetailModal from "./InsuranceDetailModal";
import CoveragePeriodCell from "./CoveragePeriodCell";
import InsuranceSummaryStrip from "./InsuranceSummaryStrip";
import InsuranceFilterChipBar, {
  type InsuranceFilterChipBarFilters,
} from "./InsuranceFilterChipBar";
import InsuranceActiveFilterSummary from "./InsuranceActiveFilterSummary";
import {
  useArchiveTrackingItem,
  useCreateTrackingItem,
  useTrackingItem,
  useTrackingItems,
  useUpdateTrackingItem,
} from "../../../../hooks/useTracking";
import { useInsuranceHistorical } from "../../../../hooks/useInsuranceHistorical";
import type {
  CreateInsuranceInput,
  Insurance,
  UpdateInsuranceInput,
} from "../../../../types";
import { INSURANCE_CATEGORY_LABELS } from "../../../../types";
import { deriveInsuranceStatus } from "../../../../utils/deriveInsuranceStatus";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { formatAud0dp } from "../../../../utils/formatCurrency";

/** True when an insurance's declared coverage sits below the contract's
 *  minimum requirement. Only meaningful for category='insurance' — licences
 *  and accreditations have no minimumLimit concept. */
function isBelowMinimum(i: Insurance): boolean {
  return (
    i.category === "insurance" &&
    typeof i.coverageValue === "number" &&
    typeof i.minimumLimit === "number" &&
    i.coverageValue < i.minimumLimit
  );
}

interface InsuranceTabProps {
  contractId: string;
  /** `?item=X` query param, forwarded from the page-level tab router. */
  itemIdFromUrl?: string | null;
}

// Server fetch ceiling — large enough that almost all contracts'
// insurance/licence registers fit in a single API call. Pagination
// below is client-side over the fetched set, matching the milestones /
// deliverables / meetings pattern (single fetch, slice for the page).
// The previous cursor-pagination model didn't compose with a foot-of-
// list "Showing X–Y of N" caption because cursors don't expose N.
const FETCH_LIMIT = 200;
const DEFAULT_PAGE_SIZE = 25;

/** Sortable column identifiers. The Coverage period column sorts by
 *  expiryDate (the most pressing field for renewal triage); the Value
 *  column sorts by coverageValue numerically with nulls last. */
type SortColumn = "name" | "provider" | "coveragePeriod" | "value";

const DEFAULT_SORT_COLUMN: SortColumn = "coveragePeriod";

function formatDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return v;
  }
}

const EMPTY_FILTERS: InsuranceFilterChipBarFilters = {
  category: [],
  status: [],
  provider: [],
  evidenceMissing: false,
  belowMinimum: false,
  includeArchived: false,
};

export default function InsuranceTab({
  contractId,
  itemIdFromUrl,
}: InsuranceTabProps) {
  const [chipFilters, setChipFilters] =
    useState<InsuranceFilterChipBarFilters>(EMPTY_FILTERS);
  const [searchText, setSearchText] = useState("");
  // Bumped by Clear-all so the chip-bar's local search-input state can
  // be forced to resync even when the `searchText` prop value didn't
  // change (mid-debounce reset race).
  const [filterResetCounter, setFilterResetCounter] = useState(0);
  const includeArchived = chipFilters.includeArchived;
  // Client-side pagination over the fetched set (single API call up to
  // FETCH_LIMIT records). Matches the milestone / deliverable model.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [modalMode, setModalMode] = useState<InsuranceFormMode | null>(null);
  const [modalSource, setModalSource] = useState<Insurance | undefined>();
  // Row expand supports two modes: 'view' (read-only DetailExpand rendered
  // below the row) and 'edit' (InlineExpand form). Row-click opens view;
  // pencil icon + "Edit" button in the detail header open edit. After a
  // successful Save in edit mode, the row returns to view mode with the
  // updated values (instead of collapsing) so the user sees the result.
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [expandedMode, setExpandedMode] = useState<"view" | "edit" | null>(
    null,
  );
  const [archiveTarget, setArchiveTarget] = useState<Insurance | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  const [renewSuccess, setRenewSuccess] = useState<Insurance | null>(null);
  const [detailItem, setDetailItem] = useState<Insurance | null>(null);
  const [archiveBanner, setArchiveBanner] = useState(true);
  // The Coverage period column reads as the default sort (ascending) until
  // the user explicitly applies or clears a sort — allowClear:true (the
  // hook's default) preserves the existing 3-state cycle-with-clear.
  const { sortState, ariaSortFor, handleSortClick } = useTableSort<SortColumn>(
    { column: null, direction: null, cleared: false },
    { defaultColumn: DEFAULT_SORT_COLUMN },
  );

  const { data, isPending, error } = useTrackingItems(
    contractId,
    "insurance",
    { limit: FETCH_LIMIT, includeArchived },
  );

  const items: Insurance[] = useMemo(
    () => (data?.items ?? []).filter((i): i is Insurance => i.type === "insurance"),
    [data],
  );

  const todayDate = useMemo(() => {
    const iso = getBrisbaneToday();
    return new Date(`${iso}T00:00:00Z`);
  }, []);

  const supersededMap = useInsuranceHistorical(items, { today: todayDate });

  // Row-level helpers. A superseded row is demoted (greyed) and gets the muted
  // "Expired" badge ONLY when it has actually lapsed — an expiring "Renewed"
  // row is still active coverage, so it keeps full opacity and a "Renewed" pill.
  const resolveStatus = (i: Insurance) =>
    i.derivedStatus ??
    deriveInsuranceStatus(i.expiryDate, i.startDate, i.reminderDays);
  const isSupersededRow = (i: Insurance) =>
    supersededMap.get(i.id)?.isSuperseded ?? false;
  const isExpiredSuperseded = (i: Insurance) =>
    isSupersededRow(i) && resolveStatus(i) === "expired";

  // Distinct provider names seen on this contract — fed into the form's
  // provider <datalist> so users typing a known provider get a suggestion.
  const providerSuggestions = useMemo(
    () =>
      Array.from(
        new Set(items.map((i) => i.provider).filter((p): p is string => !!p)),
      ).sort(),
    [items],
  );
  const supersededIds = useMemo(() => {
    const set = new Set<string>();
    for (const [id, entry] of supersededMap.entries()) {
      if (entry.isSuperseded) set.add(id);
    }
    return set;
  }, [supersededMap]);

  // Apply chip + search filters in memory. The backend list query is the
  // primary paging surface; chip filters narrow within the current page.
  const filteredItems = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return items.filter((i) => {
      if (chipFilters.category.length > 0 && !chipFilters.category.includes(i.category)) {
        return false;
      }
      const status =
        i.derivedStatus ??
        deriveInsuranceStatus(i.expiryDate, i.startDate, i.reminderDays);
      if (
        chipFilters.status.length > 0 &&
        (!status || !chipFilters.status.includes(status))
      ) {
        return false;
      }
      if (
        chipFilters.provider.length > 0 &&
        !chipFilters.provider.includes(i.provider)
      ) {
        return false;
      }
      if (chipFilters.evidenceMissing && (i.evidenceLinks ?? []).length > 0) {
        return false;
      }
      if (chipFilters.belowMinimum && !isBelowMinimum(i)) {
        return false;
      }
      if (search) {
        const haystack = [
          i.name,
          i.provider,
          i.policyNumber ?? "",
          i.contractClause ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [items, chipFilters, searchText]);

  // Sort the visible rows. When no user sort is applied, fall back to the
  // built-in default (expiryDate ascending) so the most pressing renewals
  // lead the list. User-applied sorts override the default.
  const sortedItems = useMemo(() => {
    const byExpiryAsc = (a: Insurance, b: Insurance) =>
      a.expiryDate.localeCompare(b.expiryDate);
    const arr = [...filteredItems];
    if (sortState.column === null) {
      arr.sort(byExpiryAsc);
      return arr;
    }
    const dir = sortState.direction === "descending" ? -1 : 1;
    const compare = (a: Insurance, b: Insurance): number => {
      switch (sortState.column) {
        case "name":
          return a.name.localeCompare(b.name) * dir;
        case "provider":
          return a.provider.localeCompare(b.provider) * dir;
        case "coveragePeriod":
          return a.expiryDate.localeCompare(b.expiryDate) * dir;
        case "value": {
          const av = typeof a.coverageValue === "number" ? a.coverageValue : null;
          const bv = typeof b.coverageValue === "number" ? b.coverageValue : null;
          // Nulls always sort last regardless of direction.
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    };
    arr.sort(compare);
    return arr;
  }, [filteredItems, sortState]);

  // Client-side pagination over the sorted set.
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedItems = useMemo(() => {
    const startIndex = (safePage - 1) * pageSize;
    return sortedItems.slice(startIndex, startIndex + pageSize);
  }, [sortedItems, safePage, pageSize]);

  // Deep-link page-jump: when ?item=<id> targets an insurance/licence on a
  // later page, jump the client-side paginator to the page that holds it, and
  // open the row's view detail expand so the linked record is readable
  // without a second click.
  const deepLinkTargetIndex = useMemo(
    () =>
      itemIdFromUrl
        ? sortedItems.findIndex((i) => i.id === itemIdFromUrl)
        : -1,
    [itemIdFromUrl, sortedItems],
  );
  useDeepLinkPageJump({
    itemId: itemIdFromUrl,
    targetIndex: deepLinkTargetIndex,
    pageSize,
    onJump: (targetPage, _index, itemId) => {
      if (targetPage !== page) setPage(targetPage);
      setHighlightRowId(itemId);
      setExpandedRowId(itemId);
      setExpandedMode("view");
    },
  });

  // F3: `deepLinkInList` (not "in page") = the item exists anywhere in the
  // loaded contract list. When true the page-jump reveals it; the missing-item
  // banner fires only for a genuinely-absent id (deleted, or archived while the
  // filter is off), which is fetched directly and surfaced via a banner.
  const deepLinkInList = itemIdFromUrl
    ? items.some((i) => i.id === itemIdFromUrl)
    : false;
  const missingItemQuery = useTrackingItem(
    contractId,
    "insurance",
    !deepLinkInList && itemIdFromUrl ? itemIdFromUrl : "",
  );
  const missingItem =
    missingItemQuery.data && missingItemQuery.data.type === "insurance"
      ? (missingItemQuery.data as Insurance)
      : null;

  const createMut = useCreateTrackingItem();
  const updateMut = useUpdateTrackingItem();
  const archiveMut = useArchiveTrackingItem();

  const handleOpenCreate = () => {
    setModalSource(undefined);
    setModalMode("create");
  };
  const handleOpenRenew = (item: Insurance) => {
    setModalSource(item);
    setModalMode("renew");
  };
  const handleCloseModal = () => setModalMode(null);

  const handleCreate = async (input: CreateInsuranceInput) => {
    const isRenew = modalMode === "renew";
    const priorSource = modalSource;
    const created = (await createMut.mutateAsync({
      contractId,
      type: "insurance",
      input,
    })) as Insurance | undefined;
    if (created?.id) {
      setHighlightRowId(created.id);
      if (isRenew && priorSource) {
        setRenewSuccess(priorSource);
      }
    }
  };

  const handleUpdate = async (input: UpdateInsuranceInput) => {
    const itemId = modalSource?.id ?? expandedRowId;
    if (!itemId) return;
    await updateMut.mutateAsync({
      contractId,
      type: "insurance",
      itemId,
      input,
    });
  };

  const handleInlineUpdate = (item: Insurance) => async (
    input: UpdateInsuranceInput,
  ) => {
    await updateMut.mutateAsync({
      contractId,
      type: "insurance",
      itemId: item.id,
      input,
    });
    // Return to view mode with the fresh values (rather than collapsing).
    setExpandedMode("view");
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!archiveTarget) return;
    await archiveMut.mutateAsync({
      contractId,
      type: "insurance",
      itemId: archiveTarget.id,
      reason,
    });
    setArchiveTarget(null);
  };

  const handleArchiveFromRenewBanner = async () => {
    if (!renewSuccess) return;
    setArchiveTarget(renewSuccess);
    setRenewSuccess(null);
  };

  // Focus-return on inline-expand collapse — the row's edit button ref is
  // stored in a map keyed by item id.
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const restoreRowFocus = (id: string) => {
    rowRefs.current[id]?.focus();
  };

  /** Open (or re-open) a row in the given mode. If the same row is already
   *  open in the same mode, collapse. Clicking a row in a different mode
   *  always switches mode (doesn't collapse). */
  const openExpand = (item: Insurance, mode: "view" | "edit") => {
    setExpandedRowId((prev) => {
      if (prev === item.id && expandedMode === mode) {
        setExpandedMode(null);
        return null;
      }
      setExpandedMode(mode);
      return item.id;
    });
  };

  const sortFor = (column: SortColumn): ColumnSort => ({
    ariaSort: ariaSortFor(column),
    onSortClick: () => handleSortClick(column),
  });

  const columns: Column<Insurance>[] = [
    {
      header: "Name",
      sort: sortFor("name"),
      cell: (i) => {
        const evidenceMissing = (i.evidenceLinks ?? []).length === 0;
        const belowMinimum = isBelowMinimum(i);
        return (
          <div className="flex flex-col gap-0.5">
            <span>{i.name}</span>
            {(i.contractClause || evidenceMissing || belowMinimum) && (
              <div className="flex flex-wrap items-center gap-2 text-caption">
                {i.contractClause && (
                  <span
                    data-testid="insurance-contract-clause-chip"
                    className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-caption text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                  >
                    {i.contractClause}
                  </span>
                )}
                {evidenceMissing && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <AlertTriangle
                      data-testid="insurance-no-evidence-glyph"
                      className="h-3 w-3"
                      aria-hidden="true"
                    />
                    no evidence
                  </span>
                )}
                {belowMinimum && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <AlertTriangle
                      data-testid="insurance-below-minimum-glyph"
                      className="h-3 w-3"
                      aria-hidden="true"
                    />
                    below minimum: {formatAud0dp(i.minimumLimit as number)}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "Type",
      cell: (i) => INSURANCE_CATEGORY_LABELS[i.category] ?? "—",
    },
    { header: "Provider", sort: sortFor("provider"), cell: (i) => i.provider },
    {
      header: "Coverage period",
      sort: sortFor("coveragePeriod"),
      cell: (i) => (
        <CoveragePeriodCell
          startDate={i.startDate}
          expiryDate={i.expiryDate}
          reminderDays={i.reminderDays}
          isHistorical={isExpiredSuperseded(i)}
        />
      ),
    },
    {
      header: "Value",
      align: "right",
      cellClassName: "tabular-nums",
      sort: sortFor("value"),
      cell: (i) =>
        typeof i.coverageValue === "number" ? formatAud0dp(i.coverageValue) : "—",
    },
    {
      header: "Status",
      cell: (i) => {
        const status = resolveStatus(i);
        return status ? (
          <TrackingStatusBadge
            kind="insurance"
            status={status}
            superseded={isSupersededRow(i)}
            successorName={supersededMap.get(i.id)?.successor?.name}
          />
        ) : (
          "—"
        );
      },
    },
    {
      header: "Actions",
      align: "right",
      cell: (i) => {
        const derivedStatus = resolveStatus(i);
        // Renew is meaningful only for live policies that are about to
        // lapse or already lapsed. Superseded (already-renewed) rows
        // already have a successor; surfacing Renew there invites
        // duplicate records.
        const showRenew =
          !isSupersededRow(i) &&
          (derivedStatus === "expiring" || derivedStatus === "expired");
        return (
          <div className="inline-flex gap-1">
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              aria-label={`Edit ${i.name}`}
              title={`Edit ${i.name}`}
              onClick={(e) => {
                e.stopPropagation();
                rowRefs.current[i.id] = e.currentTarget;
                openExpand(i, "edit");
              }}
            />
            {showRenew && (
              <Button
                variant="ghost"
                iconOnly
                size="sm"
                icon={<RotateCw className="h-4 w-4" />}
                aria-label={`Renew ${i.name}`}
                title={`Renew ${i.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenRenew(i);
                }}
              />
            )}
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              icon={<ArchiveIcon className="h-4 w-4" />}
              aria-label={`Archive ${i.name}`}
              title={`Archive ${i.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setArchiveTarget(i);
              }}
            />
          </div>
        );
      },
    },
  ];

  const handlePageChange = (next: number) => setPage(next);
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* F3: non-auto-toggling banner when ?item=X resolves to an item
          not in the current page/filter. Links to read-only Detail modal.
          User's includeArchived filter is NOT mutated. */}
      {itemIdFromUrl && !deepLinkInList && missingItem && archiveBanner && (
        <Alert
          variant="info"
          onDismiss={() => setArchiveBanner(false)}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {missingItem.archivedAt
                ? `This insurance / licence was archived on ${formatDate(
                    missingItem.archivedAt.slice(0, 10),
                  )}.`
                : `This insurance / licence is not shown in the current list.`}
            </span>
            <button
              type="button"
              className="underline"
              onClick={() => setDetailItem(missingItem)}
            >
              View archived item
            </button>
          </div>
        </Alert>
      )}

      {/* 404 on the deep-link item */}
      {itemIdFromUrl &&
        !deepLinkInList &&
        missingItemQuery.error &&
        archiveBanner && (
          <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
            Item not found. It may have been deleted.
          </Alert>
        )}

      {/* Renew-success banner with Archive-previous shortcut */}
      {renewSuccess && (
        <Alert
          variant="success"
          onDismiss={() => setRenewSuccess(null)}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span>Renewed insurance &quot;{renewSuccess.name}&quot;.</span>
            <button
              type="button"
              className="underline"
              onClick={handleArchiveFromRenewBanner}
            >
              Archive previous policy
            </button>
          </div>
        </Alert>
      )}

      <InsuranceSummaryStrip
        insurances={items}
        supersededIds={supersededIds}
        today={todayDate}
      />

      <InsuranceActiveFilterSummary
        filters={chipFilters}
        search={searchText}
        onClearAll={() => {
          setChipFilters(EMPTY_FILTERS);
          setSearchText("");
          setPage(1);
          setFilterResetCounter((c) => c + 1);
        }}
      />

      <InsuranceFilterChipBar
        filters={chipFilters}
        onFiltersChange={(next) => {
          if (next.includeArchived !== chipFilters.includeArchived) {
            setPage(1);
          }
          setChipFilters(next);
        }}
        search={searchText}
        onSearchChange={setSearchText}
        insurances={items}
        resetCounter={filterResetCounter}
      />

      <TrackingTabShell
        type="insurance"
        onAdd={handleOpenCreate}
        includeArchived={includeArchived}
        onToggleArchived={(v) => {
          setChipFilters({ ...chipFilters, includeArchived: v });
          setPage(1);
        }}
        isEmpty={!isPending && !error && sortedItems.length === 0}
        showIncludeArchived={false}
      >
        <TrackingTable<Insurance>
          columns={columns}
          items={pagedItems}
          isLoading={isPending}
          error={error}
          emptyMessage="No insurances / licences recorded yet."
          rowKey={(i) => i.id}
          expandedRowId={expandedRowId}
          highlightRowId={highlightRowId}
          onRowClick={(i) => openExpand(i, "view")}
          rowClassName={(i) =>
            isExpiredSuperseded(i)
              ? "opacity-75 hover:opacity-100 text-slate-400 dark:text-slate-500"
              : ""
          }
          renderExpandedRow={(item) =>
            expandedMode === "edit" ? (
              <InsuranceInlineExpand
                item={item}
                contractId={contractId}
                onUpdate={handleInlineUpdate(item)}
                onCancel={() => setExpandedMode("view")}
                onClosed={() => restoreRowFocus(item.id)}
                isSubmitting={updateMut.isPending}
                providerSuggestions={providerSuggestions}
              />
            ) : (
              <InsuranceDetailExpand
                item={item}
                derivedStatus={
                  item.derivedStatus ??
                  deriveInsuranceStatus(
                    item.expiryDate,
                    item.startDate,
                    item.reminderDays,
                  )
                }
                superseded={isSupersededRow(item)}
                successor={supersededMap.get(item.id)?.successor}
                today={todayDate}
                onEdit={
                  item.archivedAt ? undefined : () => setExpandedMode("edit")
                }
              />
            )
          }
        />
        {sortedItems.length > 0 && (
          <TrackingPagination
            page={safePage}
            pageSize={pageSize}
            total={sortedItems.length}
            noun="insurance / licence"
            testIdPrefix="insurance"
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </TrackingTabShell>

      <InsuranceFormModal
        isOpen={modalMode === "create" || modalMode === "renew"}
        onClose={handleCloseModal}
        mode={modalMode ?? "create"}
        contractId={contractId}
        source={modalSource}
        onCreate={handleCreate}
        isSubmitting={createMut.isPending}
        providerSuggestions={providerSuggestions}
      />
      <InsuranceFormModal
        isOpen={modalMode === "edit"}
        onClose={handleCloseModal}
        mode="edit"
        contractId={contractId}
        source={modalSource}
        onUpdate={handleUpdate}
        isSubmitting={updateMut.isPending}
        providerSuggestions={providerSuggestions}
      />

      <ArchiveDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        itemLabel={
          archiveTarget
            ? `insurance / licence "${archiveTarget.name}"`
            : "this insurance / licence"
        }
        seedKey={archiveTarget?.id}
        // Expired items need no typed justification — pre-fill a sensible
        // category-specific default so the user can just click Confirm. The
        // reason stays required + editable (the default satisfies min-3).
        defaultReason={
          archiveTarget && resolveStatus(archiveTarget) === "expired"
            ? `${INSURANCE_CATEGORY_LABELS[archiveTarget.category]} expired`
            : undefined
        }
        isSubmitting={archiveMut.isPending}
      />

      <InsuranceDetailModal
        isOpen={!!detailItem}
        onClose={() => setDetailItem(null)}
        item={detailItem}
      />
    </div>
  );
}
